import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { User } from '~/eds/src/components/User/User';
import { Department } from '~/eds/src/types/Department';
import { User as EdsUser } from '~/eds/src/types/User';
import { getUserName } from '~/eds/src/utils/getUserName';
import { api } from '~/redux/api/createApi';
import { TagType } from '~/redux/api/TagType';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { Uuid } from '~/types/Uuid';

interface Props {
  ticketId: Uuid;
}

type Entity = {
  type: 'user' | 'department';
  value: EdsUser | Department;
};

const getModalMessage = (entity: Entity) => {
  if (entity.type === 'user') {
    return (
      <FormattedMessage
        id="UseAddTicketParticipantModal.userNotParticipant"
        defaultMessage="{user} is not currently a participant in this Workflow ticket. Add this user?"
        description="Message asking to add a user as participant to workflow ticket, user = Jon Doe"
        values={{
          user: (
            <Box display="inline-block">
              <User mode="name" user={entity.value as EdsUser} />
            </Box>
          ),
        }}
      />
    );
  }

  if (entity.type === 'department') {
    const department = entity.value as Department;
    return (
      <FormattedMessage
        id="UseAddTicketParticipantModal.departmentMembers"
        defaultMessage="{department} has {count} members. Add them all as participants to this Workflow ticket?"
        description="Message asking to add department members as participants. Example: count = 5, department = any name"
        values={{
          department: (
            <Box display="inline-block">
              <Text variant="body-bold">{department.name}</Text>
            </Box>
          ),
          count: department.count,
        }}
      />
    );
  }

  return null;
};

export function useAddTicketParticipantModal({ ticketId }: Props) {
  const intl = useIntl();
  const [entity, setEntity] = useState<Nullable<Entity>>(null);
  const dispatch = useDispatch();
  const [addParticipant, { isLoading }] =
    api.endpoints.addTicketParticipants.useMutation();

  const [modal, _showModal, closeModal] = useModal({
    title: intl.formatMessage(
      {
        id: 'UseAddTicketParticipantModal.title',
        defaultMessage:
          'Add {participantCount, plural, one {participant} other {participants}}',
        description:
          'Title of modal when adding a participant to a ticket in the workflow. Example: participantCount = "1" or "2"',
      },
      {
        participantCount: entity?.type === 'department' ? 2 : 1,
      },
    ),
    children: (
      <Layout direction="column" spacing={2}>
        {entity && getModalMessage(entity)}
      </Layout>
    ),
    primaryAction: {
      onClick: onAddAsParticipantClick,
      text: intl.formatMessage(
        {
          id: 'UseAddTicketParticipantModal.addUser',
          defaultMessage: 'Add {userCount, plural, one {User} other {Users}}',
          description:
            'Button text to add user(s) as participant(s) to a ticket. Example: userCount = "1" or "2"',
        },
        {
          userCount: entity?.type === 'department' ? 2 : 1,
        },
      ),
      isLoading,
    },
    onCancel: clearEntity,
    onHide: clearEntity,
  });

  async function onAddAsParticipantClick() {
    if (entity) {
      const entityName =
        entity.type === 'user'
          ? getUserName({
              user: entity.value as EdsUser,
              intl,
            })
          : (entity.value as Department).name;
      try {
        await addParticipant({
          type: entity.type,
          ticketId,
          typeIds: [entity.value.id as number],
        }).unwrap();

        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'UseAddTicketParticipantModal.success',
              defaultMessage: '{entityName} was added as a ticket participant',
              description:
                'Success message when adding a participant to a ticket. Example: entityName = "John Smith" or "Engineering Department"',
            },
            {
              entityName,
            },
          ),
        );
        dispatch(api.util.invalidateTags([{ type: TagType.Acl, id: 'LIST' }]));
      } catch {
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'UseAddTicketParticipantModal.error',
              defaultMessage:
                'An error occured while adding {entityName} as a participant',
              description:
                'Error message when adding a participant to a ticket. Example: entityName = "John Smith" or "Engineering Department"',
            },
            { entityName },
          ),
        );
      }
      clearEntity();
      closeModal();
    }
  }

  function clearEntity() {
    setEntity(null);
  }

  function showModal(entity: {
    type: 'user' | 'department';
    value: EdsUser | Department;
  }) {
    setEntity(entity);
    _showModal();
  }

  return [modal, showModal] as const;
}
